import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Download, FileWarning, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import ReportDocument from '../components/devreport/presentation/ReportDocument';
import { buildPresentation } from '../components/devreport/presentation/presentation';
import { assetTracker } from '../components/devreport/presentation/assetTracker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SharedDevelopmentReport = ({ printMode = false }) => {
  const { capability } = useParams();
  const [data, setData] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    document.title = 'Secure Player Development Report | PFAP';
    const tags = [['robots', 'noindex, nofollow, noarchive'], ['referrer', 'no-referrer']];
    const created = tags.map(([name, content]) => {
      const meta = document.createElement('meta'); meta.name = name; meta.content = content; document.head.appendChild(meta); return meta;
    });
    return () => created.forEach((meta) => meta.remove());
  }, []);

  useEffect(() => {
    let cancelled = false;
    assetTracker.reset();
    axios.get(`${API}/development-report-share/${capability}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) { setUnavailable(true); window.__PDF_ERROR__ = true; } });
    return () => { cancelled = true; };
  }, [capability]);

  useEffect(() => {
    if (!data || !printMode) return undefined;
    const start = Date.now();
    const timer = setInterval(() => {
      if ((Date.now() - start > 500 && assetTracker.pending() === 0) || Date.now() - start > 12000) {
        clearInterval(timer); window.__PDF_BROKEN_ASSETS__ = assetTracker.broken(); window.__PDF_READY__ = true;
      }
    }, 150);
    return () => clearInterval(timer);
  }, [data, printMode]);

  if (unavailable) return (
    <main className="flex min-h-screen items-center justify-center bg-slate-100 px-6" data-testid="shared-report-unavailable">
      <div className="max-w-md text-center"><FileWarning className="mx-auto h-10 w-10 text-slate-400" /><h1 className="mt-4 text-2xl font-semibold text-slate-800">This report is unavailable.</h1></div>
    </main>
  );
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-500" data-testid="shared-report-loading"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading report…</div>;

  const pres = buildPresentation(data);
  const status = pres.report.status;
  return (
    <div className="min-h-screen bg-slate-100" data-testid="shared-report-page">
      {!printMode && (
        <header className="no-print sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur" data-testid="shared-report-header">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div className="flex items-center gap-3"><img src="/pfap-logo-full.png" alt="PFAP" className="h-8 w-auto" /><div><div className="text-sm font-semibold text-slate-800">Player Development Report</div><div className="text-xs text-slate-400">Secure external review</div></div></div>
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${status === 'draft' ? 'bg-amber-100 text-amber-800' : status === 'archived' ? 'bg-slate-200 text-slate-700' : 'bg-emerald-100 text-emerald-700'}`} data-testid="shared-report-status">{status === 'draft' ? 'DRAFT — NOT FINAL' : status === 'archived' ? 'ARCHIVED' : 'FINALIZED'}</span>
              <Button asChild size="sm" data-testid="shared-report-download-pdf"><a href={`${API}/development-report-share/${capability}/pdf`}><Download className="mr-1.5 h-4 w-4" /> Download Report PDF</a></Button>
            </div>
          </div>
        </header>
      )}
      <div className={printMode ? '' : 'py-6'}>
        <ReportDocument report={pres.report} sections={pres.sections} overview={pres.overview} lead={pres.lead} printMode={printMode} externalReview />
      </div>
    </div>
  );
};

export default SharedDevelopmentReport;